import { Box, Typography, Button } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { MenuData } from "../../components/input/CustomSelect";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "../../components/modal/CustomModal";
import alertService from "../../components/alert/alertService";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { useForm } from "react-hook-form";
import { CustomAutoComplete } from "../../components/input/CustomAutoComplete";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { common, input } from "../../i18n";
import AssetsService from "../../service/assets.service";
import { AssetsType, AssetType } from "../../models/Asset";
import { DROPDOWN_EMISSION_GROUP } from "../../components/emission/emission-components";
import {
  STATUS,
  ASSET_STATUS_ALLOWED_EMISSION_GROUPS,
  ASSET_STATUS_EMISSION_GROUP_ERROR_KEY,
} from "../../constants";

export type AddAssetFormValues = {
  organization: string;
  subOrganization: string;
  emissionGroup: string;
  site: string;
  assetType: string;
  assetName: string;
  assetStatus: string;
};

const assetStatusMenuItem: MenuData[] = [
  {
    nameTH: "เป็นเจ้าของและใช้งานเอง",
    nameEN: "Owned and used",
    value: STATUS.ASSET.OWNED_AND_USED,
  },
  // {
  //   nameTH: "เช่าจากผู้อื่น",
  //   nameEN: "Leased in",
  //   value: "leased in",
  // },
  {
    nameTH: "เป็นเจ้าของและให้ผู้อื่นเช่า",
    nameEN: "Owned and leased out",
    value: STATUS.ASSET.OWNED_AND_LEASED_OUT,
  },
  {
    nameTH: "จ้างหน่วยงานภายนอก",
    nameEN: "Outsourced",
    value: STATUS.ASSET.OUTSOURCED,
  },
];

interface Props {
  editItem: any | undefined;
  open: boolean;
  onClose: () => void;
  currentOrganization?: string;
  organizationsMenuItem: { nameTH: string; nameEN: string; value: string }[];
  emissionGroup: { nameTH: string; nameEN: string; value: string }[];
  selectedSubOrganization?: string;
  subOrganizationsMenuItem: { nameTH: string; nameEN: string; value: string }[];
  assetType: { nameTH: string; nameEN: string; value: string }[];
  onAddClick: (
    organization?: string,
    emissionGroup?: string,
    subOrganization?: string,
    assetType?: string,
    assetName?: string,
    assetStatus?: string,
    site?: string,
  ) => void;
  onEditClick: (
    assetType?: string,
    assetName?: string,
    assetStatus?: string,
    site?: string,
  ) => void;
}

const AddAssetModal = (props: Props) => {
  const { t, i18n } = useTranslation([common, input], { nsMode: "fallback" });

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    trigger,
    formState: { isValid, isDirty },
  } = useForm<AddAssetFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    shouldUnregister: false,
  });

  const organization = watch("organization");

  const emissionGroup = watch("emissionGroup");

  const subOrganization = watch("subOrganization");

  const site = watch("site");

  const assetType = watch("assetType");

  const assetName = watch("assetName");

  const assetStatus = watch("assetStatus");

  const skipAssetTypeReset = useRef(false);
  const skipSiteReset = useRef(false);

  const clearData = () => {
    skipAssetTypeReset.current = true;
    skipSiteReset.current = true;

    const emission = DROPDOWN_EMISSION_GROUP.find(
      (e) => e.emissionGroup === props.editItem?.emissionGroup,
    );

    const subOrgValue =
      props.selectedSubOrganization !== undefined
        ? props.selectedSubOrganization
        : props.subOrganizationsMenuItem.length === 1
          ? props.subOrganizationsMenuItem[0].value
          : `${props.editItem?.subOrganization.id}`;

    reset({
      organization: props.currentOrganization ?? "",
      emissionGroup: emission?._id ?? "",
      subOrganization: subOrgValue,
      assetType: props.editItem?.assetType ?? "",
      assetName: props.editItem?.name ?? "",
      assetStatus: props.editItem?.status ?? assetStatusMenuItem[0].value,
      site: "",
    });

    setDefaultSite();
    setAssetTypes([]);
    if (props.editItem !== undefined) trigger();
    // Clear skip flags after the next animation frame in case watched values
    // didn't change (effects won't fire, flags would stay set permanently)
    requestAnimationFrame(() => {
      skipAssetTypeReset.current = false;
      skipSiteReset.current = false;
    });
  };

  useEffect(() => {
    clearData();
  }, [props.editItem]);

  useEffect(() => {
    setValue("organization", props.currentOrganization ?? "");
  }, [props.currentOrganization]);

  useEffect(() => {
    if (props.selectedSubOrganization !== undefined) {
      setValue("subOrganization", props.selectedSubOrganization);
    } else if (props.subOrganizationsMenuItem.length === 1) {
      setValue("subOrganization", props.subOrganizationsMenuItem[0].value);
    }

    if (context?.selectedSite !== undefined) {
      const result = context.subOrganizations
        ?.find((s) => s.id === context.selectedSubOrganization)
        ?.sites.map((s) => ({
          nameEN: s.name,
          nameTH: s.name,
          value: s.refId,
        }));

      setSiteMenu(result ?? []);

      setDefaultSite();
    }
  }, [props.selectedSubOrganization, props.subOrganizationsMenuItem]);

  const onAdd = async () => {
    if (props.editItem === undefined) {
      props.onAddClick(
        organization,
        emissionGroup,
        subOrganization,
        assetType,
        assetName,
        assetStatus,
        site,
      );
    } else {
      if (assetStatus !== props.editItem?.status) {
        const confirmed = await alertService.confirm(
          t("Confirm-Asset-Status-Change"),
          t("Confirm-Asset-Status-Change-Body"),
          t("Confirm"),
          "var(--dark)",
          true,
          undefined,
          t("Cancel"),
          "var(--white)",
        );
        if (!confirmed) return;
      }
      props.onEditClick(assetType, assetName, assetStatus, site);
    }
    clearData();
  };

  const onClose = () => {
    props.onClose();
    clearData();
  };

  const context = useContext(OrganizationContext);

  if (!context) {
    throw Error();
  }

  const [siteMenu, setSiteMenu] = useState<MenuData[]>([]);

  const setDefaultSite = () => {
    let currentSite;

    if (props.editItem === undefined) {
      currentSite = context.subOrganizations
        ?.find((s) => s.id === context.selectedSubOrganization)
        ?.sites.find((s) => `${s.id}` === `${context.selectedSite}`);
    } else {
      currentSite = context.subOrganizations
        ?.find((s) => s.id === props.editItem.subOrganization.id)
        ?.sites.find((s) => s.name === props.editItem.siteName);
    }

    setValue("site", currentSite ? `${currentSite?.refId}` : "");
  };

  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);


  useEffect(() => {
    if (context?.selectedSite === undefined) {
      setValue("site", "");
    }
  }, [context?.selectedSite]);

  useEffect(() => {
    const result = context.subOrganizations
      ?.find((s) => {
        return `${s.id}` === `${subOrganization}`;
      })
      ?.sites.map((s) => ({
        nameEN: s.name,
        nameTH: s.name,
        value: s.refId,
      }));

    if (result?.length === 1) {
      setValue("site", result[0].value, { shouldValidate: true });
    } else if (!skipSiteReset.current) {
      setValue("site", "", { shouldValidate: true });
    }
    skipSiteReset.current = false;

    setSiteMenu(result ?? []);
  }, [subOrganization]);

  useEffect(() => {
    let cancelled = false;
    if (emissionGroup) {
      AssetsService.getAssetTypes(emissionGroup as AssetsType).then(
        (types) => { if (!cancelled) setAssetTypes(types); },
      );
    } else {
      setAssetTypes([]);
    }
    if (skipAssetTypeReset.current) {
      skipAssetTypeReset.current = false;
    } else {
      setValue("assetType", "");
    }
    trigger("assetStatus");
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionGroup]);

  return (
    <CustomModal
      header={
        <Typography variant="h1">
          {props.editItem === undefined ? t("Add-Asset") : t("Edit-Asset")}
        </Typography>
      }
      body={
        <form onSubmit={handleSubmit(onAdd)}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={"44px"}
            width={"100%"}
          >
            <Grid2
              container
              rowSpacing={"20px"}
              columnSpacing={{ xs: "0px", sm: "40px" }}
              disableEqualOverflow
            >
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Organization"),
                    }),
                  }}
                  name="organization"
                  control={control}
                  label={t("Organization")}
                  info={t("Organization")}
                  placeholder={t("Select-Param", {
                    param: t("Organization"),
                  })}
                  listMenuItem={props.organizationsMenuItem}
                  defaultValue={""}
                  disabled
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Emission-Group"),
                    }),
                  }}
                  name="emissionGroup"
                  control={control}
                  label={t("Emission-Group")}
                  info={t("Emission-Group")}
                  placeholder={t("Select-Param", {
                    param: t("Emission-Group"),
                  })}
                  listMenuItem={props.emissionGroup}
                  defaultValue={""}
                  disabled={props.editItem !== undefined}
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Sub-Organizations"),
                    }),
                  }}
                  name="subOrganization"
                  control={control}
                  label={t("Sub-Organizations")}
                  info={t("Sub-Organizations")}
                  placeholder={t("Select-Param", {
                    param: t("Sub-Organizations"),
                  })}
                  listMenuItem={props.subOrganizationsMenuItem}
                  defaultValue={props.selectedSubOrganization ?? ""}
                  disabled={
                    props.selectedSubOrganization !== undefined ||
                    props.editItem !== undefined
                  }
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Site"),
                    }),
                  }}
                  name="site"
                  control={control}
                  label={t("Site")}
                  info={t("Site")}
                  placeholder={t("Select-Param", {
                    param: t("Site"),
                  })}
                  listMenuItem={siteMenu}
                  defaultValue={""}
                  disabled={
                    subOrganization === "" ||
                    subOrganization === undefined ||
                    props.editItem !== undefined ||
                    context.selectedSite !== undefined
                  }
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  name={"assetType"}
                  control={control}
                  label={t("Asset-Type")}
                  info={t("Asset-Type")}
                  placeholder={t("Asset-Type")}
                  required
                  listMenuItem={assetTypes.map((a) => ({
                    nameEN: a.en,
                    nameTH: a.th,
                    value: i18n.language === "th" ? a.th : a.en,
                  }))}
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Asset-Type"),
                    }),
                    pattern: {
                      value: /^[^,]*$/,
                      message: t("Name-Fails-To-Match-Pattern"),
                    },
                  }}
                  freeSolo={true}
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <InputReactHookForm
                  required
                  name={"assetName"}
                  control={control}
                  label={t("Asset-Name")}
                  info={t("Asset-Name")}
                  placeholder={t("Asset-Name")}
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Asset-Name"),
                    }),
                    pattern: {
                      value: /^[^,]*$/,
                      message: t("Name-Fails-To-Match-Pattern"),
                    },
                  }}
                  defaultValue={""}
                />
              </Grid2>
              <Grid2 xs={12} sm={6}>
                <CustomAutoComplete
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Asset-Status"),
                    }),
                    validate: (value) => {
                      const allowedGroups =
                        ASSET_STATUS_ALLOWED_EMISSION_GROUPS[value];
                      if (allowedGroups) {
                        const allowedIds = DROPDOWN_EMISSION_GROUP.filter((e) =>
                          allowedGroups.includes(e.emissionGroup),
                        ).map((e) => e._id);
                        if (!allowedIds.includes(emissionGroup)) {
                          return t(
                            ASSET_STATUS_EMISSION_GROUP_ERROR_KEY[value],
                          );
                        }
                      }
                      return true;
                    },
                  }}
                  name="assetStatus"
                  control={control}
                  label={t("Asset-Status")}
                  info={t("Asset-Status")}
                  placeholder={t("Select-Param", {
                    param: t("Asset-Status"),
                  })}
                  listMenuItem={assetStatusMenuItem}
                  defaultValue={""}
                />
              </Grid2>
            </Grid2>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              type="submit"
              disabled={!isValid || (props.editItem !== undefined && !isDirty)}
            >
              {props.editItem === undefined ? t("Add") : t("Save")}
            </Button>
          </Box>
        </form>
      }
      open={props.open}
      onClose={onClose}
    />
  );
};

export default AddAssetModal;
